import { DocumentNode, useLazyQuery } from "@apollo/client";
import { useDebounce } from "@lyfegen-platform/react-common";
import { AutocompleteInputChangeReason } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { escapeRegexSyntax } from "../utils/escape-regex-syntax";

export const minSearchTermLength = 3;

export default function useAsyncSearch<TResult, TItem>(
	query: DocumentNode,
	selectedItem: TItem | null,
	mapResult: (data?: TResult) => TItem[],
	compare: (a: TItem, b: TItem) => boolean,
	variables?: any
) {
	const [load, { loading, data, error }] = useLazyQuery<TResult>(query);
	const [searchTerm, setSearchTerm] = useState("");
	const debouncedTerm = useDebounce(searchTerm);
	const [items, setItems] = useState<TItem[]>([]);

	useEffect(() => {
		if (debouncedTerm.length >= minSearchTermLength) {
			load({ variables: { id: debouncedTerm, token: `(?i).*${escapeRegexSyntax(debouncedTerm)}.*`, ...variables } });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedTerm, load]);

	useEffect(() => {
		if (!debouncedTerm.length || data) {
			const foundItems = !debouncedTerm.length ? [] : mapResult(data);
			setItems(foundItems.filter((item) => !selectedItem || !compare(selectedItem, item)));
		}
	}, [debouncedTerm, data, selectedItem, mapResult, compare]);

	function handleSetSearchTerm(_: any, term: string, _reason: AutocompleteInputChangeReason) {
		if (_reason !== "reset") setSearchTerm((term || "").trim());
	}

	return {
		loading: loading || (searchTerm.length >= minSearchTermLength && debouncedTerm !== searchTerm),
		data,
		error,
		items,
		searchTerm,
		debouncedTerm,
		handleSetSearchTerm,
	};
}
